'use client'

import { createContext, useContext, type ReactNode } from 'react'

import type { FormatInputs } from '@/lib/content-standard/format'

const FormatContext = createContext<FormatInputs | undefined>(undefined)

/**
 * The page's formatting decision for its client islands, mounted by the page's frame from
 * the same resolution it sets for the server's read — never by the shared layout, which a
 * client-side navigation does not re-render, so a decision held there would outlive its
 * page. It carries the decision as data; each island builds its formatters from it.
 */
export function FormatProvider({
    inputs,
    children,
}: {
    readonly inputs: FormatInputs
    readonly children: ReactNode
}) {
    return <FormatContext.Provider value={inputs}>{children}</FormatContext.Provider>
}

/** The page's formatting decision. Throws outside a provider: an island stating a value
 *  canonical on a page that converts is the mixed-currency page. */
export function useFormatInputs(): FormatInputs {
    const inputs = useContext(FormatContext)
    if (inputs === undefined) {
        throw new Error(
            'useFormatInputs reads the page frame’s formatting decision: mount a FormatProvider in the page',
        )
    }
    return inputs
}
